import { useEffect, useRef, useState } from 'react';

// LIBS
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

// SERVICES
import {
  convertToDateDefault,
  trackEventMatomoVisit,
  trackEventMatomo,
} from 'js/library/utils/helpers';
import { useCurrentUserInfo } from 'js/library/services/currentUserInfo';

// STYLES
import {
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Modal,
  Button,
  CircularProgress,
  Checkbox,
  Divider,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Box,
} from '@mui/material';

// ASSETS
import alert from 'styles/assets/Messages/Alert.webp';
import establishment from 'styles/assets/Messages/Establishment.webp';
import news from 'styles/assets/Messages/News.webp';
import promotion from 'styles/assets/Messages/Promotion.webp';
import triibomania from 'styles/assets/Messages/Triibomania.webp';
import provider from 'styles/assets/Messages/Provider.webp';
import tribomania from 'styles/assets/Messages/Tribomania.webp';

// COMPONENTS
import GoBack from 'js/containers/GoBack';

// APIS
import getUserInboxMessages from 'js/library/utils/API/getUserInboxMessages';
import readNotification from 'js/library/utils/API/readNotification';
import deleteInbox from 'js/library/utils/API/deleteInbox';

export default function Inbox() {
  const { userInfo, loading: userInfoLoading } = useCurrentUserInfo();
  const isGuest = !userInfo || userInfo.triiboId?.indexOf('@guest-triibo.com.br') > 0;
  const { t } = useTranslation('app');

  // Ícones globais
  useEffect(() => {
    window['alert'] = alert;
    window['establishment'] = establishment;
    window['news'] = news;
    window['promotion'] = promotion;
    window['triibomania'] = triibomania;
    window['tribomania'] = tribomania;
    window['provider'] = provider;
    document.title = 'Caixa de entrada';
    trackEventMatomoVisit('CaixaDeEntrada');
  }, []);

  const availableIcons = [
    'establishment',
    'news',
    'promotion',
    'triibomania',
    'tribomania',
    'provider',
  ];

  const today = new Date();
  const todayISO = today.toISOString().slice(0, 10);
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(today.getDate() - 7);
  const defaultStartDate = oneWeekAgo.toISOString().slice(0, 10);

  const [messages, setMessages] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [messageSelected, setMessageSelected] = useState(null);
  const [deletedMessages, setDeletedMessages] = useState([]);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(todayISO);
  const [limit, setLimit] = useState(20);
  const [isLoading, setIsLoading] = useState(false);

  const listRef = useRef(null);

  const fetchMessages = async (opts = {}) => {
    if (!userInfo?.uId) return;

    const sDate = opts.startDate ?? startDate;
    const eDate = opts.endDate ?? endDate;
    const lim = opts.limit ?? limit;

    if (lim > 100) return;

    const startAtTs = String(new Date(sDate + 'T00:00:00').getTime());
    const endAtTs = String(new Date(eDate + 'T23:59:59').getTime());

    const origin = ['triibo', 'system'];

    setIsLoading(true);
    try {
      const result = await getUserInboxMessages(userInfo.uId, origin, startAtTs, endAtTs, lim);
      setMessages(Array.isArray(result) ? result : []);
    } catch (_err) {
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Carrega inicialmente
  useEffect(() => {
    if (!isGuest) {
      fetchMessages({ startDate, endDate, limit });
    }
    // eslint-disable-next-line
  }, [userInfo?.uId, isGuest]);

  // Ler mensagem
  const readMessages = (index) => {
    if (!messages || !messages[index]) return;
    const auxMessages = [...messages];
    if (auxMessages[index].read === undefined) {
      readNotification(userInfo.uId, auxMessages[index].id);
      auxMessages[index].read = new Date().getTime();
    }
    setMessages(auxMessages);
    setOpenModal(true);
    setMessageSelected(auxMessages[index]);
  };

  // Selecionar mensagens para exclusão
  const editMessages = (index) => (event) => {
    const auxDeletedMessages = [...deletedMessages];
    if (event.target.checked) {
      auxDeletedMessages.push(index);
    } else {
      const itemIndex = auxDeletedMessages.indexOf(index);
      if (itemIndex !== -1) auxDeletedMessages.splice(itemIndex, 1);
    }
    setDeletedMessages(auxDeletedMessages);
  };

  // Excluir mensagens selecionadas
  const deleteMessages = async () => {
    const auxMessages = [...messages];
    const auxDeletedMessages = [...deletedMessages];

    // Apagar mensagens selecionadas
    const sorted = [...auxDeletedMessages].sort((a, b) => b - a);

    for (const item of sorted) {
      const msg = auxMessages[item];
      if (msg) {
        try {
          await deleteInbox(userInfo.uId, msg.id);
          auxMessages.splice(item, 1);
          toast.success('Mensagem excluída com sucesso!');
        } catch (e) {
          console.error('Ocorreu um erro na deleteInbox', e);
          toast.error('Não foi possível excluir a mensagem. Tente novamente mais tarde.');
        }
      }
    }

    setDeletedMessages([]);
    setMessages(auxMessages);
    trackEventMatomo('caixaDeEntrada', 'tocar', 'button', 'Excluir Mensagens Selecionadas');
  };

  // Scroll infinito
  let timeDebounce = null;
  function scrollMatomo() {
    clearTimeout(timeDebounce);
    timeDebounce = setTimeout(() => {
      trackEventMatomo('Caixa de entrada', 'scroll', 'lista', 'lista de mensagens');
    }, 400);
  }

  const handleListScroll = (e) => {
    scrollMatomo();

    const target = e.target;
    const threshold = 60;
    const atBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;

    // Verifica se há menos mensagens do que o limite atual
    // Isso indica que não há mais mensagens para carregar
    const hasFewerMessagesThanLimit = messages.length < limit;

    // Só faz a requisição se:
    // 1. Não estiver carregando
    // 2. Estiver no final da lista
    // 3. O limite for menor que 100
    // 4. A quantidade atual de mensagens for igual ao limite (ou seja, pode haver mais mensagens)
    if (!isLoading && atBottom && limit < 100 && !hasFewerMessagesThanLimit) {
      const newLimit = Math.min(limit + 20, 100);
      setLimit(newLimit);
      // busca imediatamente mais mensagens apenas via scroll
      fetchMessages({ startDate, endDate, limit: newLimit });
    }
  };

  // Filtros
  const handleApplyFilter = () => {
    const safeLimit = Math.min(limit, 100);
    fetchMessages({ startDate, endDate, limit: safeLimit });

    if (listRef.current && limit === 20) {
      listRef.current.scrollTop = 0;
    }

    trackEventMatomo('caixaDeEntrada', 'tocar', 'button', 'applyFilters');
  };

  const handleClearFilter = async () => {
    setStartDate(defaultStartDate);
    setEndDate(todayISO);
    setLimit(20);

    if (listRef.current) listRef.current.scrollTop = 0;

    await fetchMessages({ startDate: defaultStartDate, endDate: todayISO, limit: 20 });

    trackEventMatomo('caixaDeEntrada', 'tocar', 'button', 'clearFilters');
  };

  if (userInfoLoading) {
    return <CircularProgress />;
  }

  if (isGuest) {
    return <Navigate to="/" />;
  } else {
    return (
      <div className="paper-container">
        <div style={{ display: 'flex' }}>
          <GoBack />
          <Typography
            variant="h6"
            style={{
              fontWeight: 'bolder',
              width: '85%',
              textAlign: 'center',
            }}
          >
            {t('messages.messages')}
          </Typography>
        </div>

        <Divider color="secondary" style={{ margin: '10px 0px' }} />

        {/* Filtros */}
        <Paper sx={{ padding: 2, marginBottom: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
            gap={2}
          >
            <Box display="flex" gap={2}>
              <TextField
                label="Data início"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label="Data fim"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <InputLabel id="limit-label">Limite</InputLabel>
                <Select
                  labelId="limit-label"
                  value={limit}
                  label="Limite"
                  onChange={(e) => setLimit(Number(e.target.value))}
                >
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={40}>40</MenuItem>
                  <MenuItem value={60}>60</MenuItem>
                  <MenuItem value={80}>80</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" gap={2}>
              <Button variant="contained" color="primary" onClick={handleApplyFilter}>
                {t('messages.apply_filters')}
              </Button>
              <Button variant="outlined" onClick={handleClearFilter}>
                {t('messages.clear_filters')}
              </Button>
            </Box>
          </Stack>
        </Paper>

        <div
          style={{
            height: 'calc(100vh - 360px)',
            flexFlow: 'column nowrap',
            display: 'flex',
          }}
        >
          {isLoading && messages === null ? (
            <Box display="flex" justifyContent="center" alignItems="center" width="100%">
              <CircularProgress size={30} />
            </Box>
          ) : messages === null || messages.length === 0 ? (
            <Typography variant="h6" style={{ padding: '30px', color: '#707070' }}>
              <span>{t('messages.you_dont_have_messages')}</span>
            </Typography>
          ) : (
            <List
              onScroll={handleListScroll}
              ref={listRef}
              className="scrollable messagesScroll"
              style={{ overflowY: 'auto' }}
            >
              {messages.map(function (item, i) {
                const iconType =
                  item?.type && availableIcons.includes(item.type) ? item.type : 'alert';

                return (
                  <div key={item.tableId}>
                    <ListItem
                      style={{ cursor: 'pointer' }}
                      onClick={() => readMessages(i)}
                      alignItems="flex-start"
                    >
                      <ListItemAvatar>
                        <img
                          width="60%"
                          className="img-radius"
                          src={window[iconType]}
                          alt="notificacoes"
                        />
                      </ListItemAvatar>
                      <div style={{ width: '60%' }}>
                        <ListItemText
                          key={item.key}
                          primary={
                            <Typography
                              style={
                                item.read === undefined
                                  ? { fontWeight: 'bold', color: '#222' }
                                  : { color: '#707070' }
                              }
                              variant="subtitle2"
                            >
                              {item.title}
                            </Typography>
                          }
                          secondary={
                            <span style={{ color: '#707070' }}>
                              {item.message && item.message.length > 110
                                ? item.message.slice(0, 109) + '...'
                                : item.message}
                            </span>
                          }
                        />
                      </div>
                      <ListItemSecondaryAction style={{ color: '#707070' }}>
                        <Checkbox
                          style={{ color: '#707070' }}
                          value="checkedA"
                          inputProps={{ 'aria-label': 'Checkbox A' }}
                          onChange={editMessages(i)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </div>
                );
              })}
              {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
                  <CircularProgress size={20} />
                </div>
              )}
            </List>
          )}

          <Button
            variant="contained"
            color="primary"
            style={{
              marginTop: 10,
              display: deletedMessages.length === 0 ? 'none' : 'block',
            }}
            onClick={() => deleteMessages()}
          >
            {t('messages.delete_selected')}
          </Button>
        </div>

        {messageSelected !== null ? (
          <Modal
            onClose={() => {
              setOpenModal(false);
              trackEventMatomo('caixaDeEntrada', 'tocar', 'overlayDiv', 'closeMessageModal');
            }}
            open={openModal}
          >
            <Paper
              sx={{
                position: 'absolute',
                padding: '20px',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '80%', sm: '70%', md: '50%' },
                maxWidth: '600px',
              }}
            >
              <Grid container>
                <Grid size={12} sx={{ textAlign: 'right' }}>
                  <IconButton
                    size="small"
                    style={{ color: 'black' }}
                    onClick={() => {
                      setOpenModal(false);
                      trackEventMatomo('caixaDeEntrada', 'tocar', 'button', 'closeMessageModal');
                    }}
                  >
                    X
                  </IconButton>
                </Grid>
                <Grid size={12} sx={{ textAlign: 'center' }}>
                  <img
                    style={{
                      width: '50%',
                      maxWidth: 60,
                    }}
                    className="img-radius"
                    src={
                      window[
                        messageSelected?.type && availableIcons.includes(messageSelected.type)
                          ? messageSelected.type
                          : 'alert'
                      ]
                    }
                    alt="notificacoes"
                  />
                </Grid>

                <Grid size={12} sx={{ textAlign: 'center', marginBottom: '15px' }}>
                  <Typography variant="subtitle2">
                    {convertToDateDefault(messageSelected.creationDate)}
                  </Typography>
                </Grid>
                <Divider />
                <Grid size={12}>
                  <Typography style={{ fontWeight: 'bold' }} variant="subtitle1">
                    {messageSelected.title}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" style={{ whiteSpace: 'pre-line' }}>
                    {messageSelected.message}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  {messageSelected.url && (
                    <Button
                      onClick={() => window.open(messageSelected.url, 'new')}
                      fullWidth={true}
                      color="primary"
                      style={{
                        textTransform: 'capitalize',
                      }}
                      className="delete-button"
                      variant="contained"
                    >
                      <span style={{ fontWeight: '550', color: 'white' }}>
                        {t('messages.show_details')}
                      </span>
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Modal>
        ) : null}
      </div>
    );
  }
}
